import { useState, useCallback, useRef } from 'react';
import { TranscriptMessage } from '@/types/speechRecognition';
import { apiConfig } from '@/lib/env';

export const useBackendTranscription = (callSid: string | null, callInProgress: boolean, remotePhoneNumber?: string) => {
  const [backendTranscripts, setBackendTranscripts] = useState<TranscriptMessage[]>([]);
  const [backendWs, setBackendWs] = useState<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();

  const connectToBackendTranscription = useCallback(() => {
    console.log('🔌 [BACKEND] Attempting to connect with:', { callSid, callInProgress });
    console.log('🔌 [BACKEND] User ID 2 - Admin level access');
    
    if (!callSid) {
      console.warn('🔌 [BACKEND] ❌ No CallSid available, cannot connect to backend transcription');
      return;
    }
    
    if (backendWs?.readyState === WebSocket.OPEN) {
      console.log('🔌 [BACKEND] ✅ Already connected, skipping connection attempt');
      return;
    }
    
    try {
      const wsUrl = `${apiConfig.wsUrl}/twilio/stream`;
      console.log('🔌 [BACKEND] 📡 Connecting to WebSocket URL:', wsUrl);
      const ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        console.log('🔌 [BACKEND] ✅ WebSocket connection established for CallSid:', callSid);
        setBackendWs(ws);
      };
      
      ws.onmessage = (event) => {
        console.log('🔌 [BACKEND] 📨 Received message:', event.data);
        try {
          const data = JSON.parse(event.data);
          console.log('🔌 [BACKEND] 📋 Parsed data:', data);
          
          if (data.type === 'transcript' && data.text) {
            // Map backend speaker labels to frontend speaker types
            // Backend sends "CALLER" for the user and "RECEIVER" for the other party
            let speaker: 'caller' | 'receiver' = 'caller'; // Default to caller
            
            console.log('🔌 [BACKEND] 🎭 Raw speaker from backend:', data.speaker);
            
            if (data.speaker) {
              const backendSpeaker = data.speaker.toLowerCase();
              console.log('🔌 [BACKEND] 🎭 Processed speaker:', backendSpeaker);
              
              // Backend mapping: 
              // "CALLER" = user speaking
              // "RECEIVER" = other person speaking  
              speaker = backendSpeaker === 'caller' ? 'caller' : 'receiver';
            }
            
            console.log('🔌 [BACKEND] 🎭 Final speaker mapping:', speaker);
            
            const newMessage: TranscriptMessage = {
              id: `backend-${Date.now()}`,
              text: data.text,
              timestamp: new Date(),
              speaker,
              source: 'backend',
              confidence: data.confidence,
              phoneNumber: remotePhoneNumber,
              participantType: speaker === 'caller' ? 'local' : 'remote'
            };
            
            console.log('🔌 [BACKEND] ➕ Adding transcript message:', newMessage);
            setBackendTranscripts(prev => [...prev, newMessage]);
          }
        } catch (error) {
          console.error('🔌 [BACKEND] ❌ Error parsing message:', error);
        }
      };
      
      ws.onerror = (error) => {
        console.error('🔌 [BACKEND] ❌ WebSocket error:', error);
      };
      
      ws.onclose = (event) => {
        console.log('🔌 [BACKEND] 🔌 WebSocket closed:', { 
          code: event.code, 
          reason: event.reason, 
          wasClean: event.wasClean 
        });
        
        if (event.code === 1006 || event.code === 1015) {
          console.error('🔌 [BACKEND] ❌ Connection rejected - likely RBAC permission issue');
          console.log('🔌 [BACKEND] Required permission may be missing for user ID 2');
        }
        
        setBackendWs(null);
        
        // Don't auto-reconnect on permission errors
        if (callInProgress && callSid && event.code !== 1006 && event.code !== 1015) {
          console.log('🔄 [BACKEND] ♻️ Scheduling reconnection in 3 seconds...');
          reconnectTimeoutRef.current = setTimeout(() => {
            connectToBackendTranscription();
          }, 3000);
        }
      };
    } catch (error) {
      console.error('🔌 [BACKEND] ❌ Failed to connect:', error);
    }
  }, [callSid, callInProgress, backendWs, remotePhoneNumber]);

  const disconnectFromBackendTranscription = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    
    if (backendWs) {
      backendWs.close();
      setBackendWs(null);
    }
  }, [backendWs]);

  const clearBackendTranscripts = useCallback(() => {
    setBackendTranscripts([]);
  }, []);

  return {
    backendTranscripts,
    backendConnected: backendWs?.readyState === WebSocket.OPEN,
    connectToBackendTranscription,
    disconnectFromBackendTranscription,
    clearBackendTranscripts
  };
};